import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getFacilityReservationTime } from "src/api/facility/facility-api";
import { ContractReservationResponse, ContractReservationUnit, FrontContractFacilityData } from "src/api/facility/facility-type";
import { useApiOperation } from "src/api/hooks";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import { useNavigateGoBack } from "src/pages/hooks/navigate-go-back";
import { useIFCUnsupportedRedirect } from "src/pages/mypage/contract/hooks/useIFCUnsupportedRedirect";
import { makeTimeString, ViewYmdFormat } from "src/utils/common-util";

const FacilityTime = () => {
  const { t } = useTranslation(["facility", "common"]);
  const { goBack } = useNavigateGoBack();

  const ReservationFacility = [
    {
      label: t("meetingRoom"),
      value: "MEETING_ROOM",
    },
    {
      label: t("desk"),
      value: "DESK",
    },
    {
      label: t("refreshRoom"),
      value: "REFRESH_ROOM",
    },
  ];
  const { contractId } = useParams();

  // IFC 환경에서는 '공용공간 이용시간'을 제공하지 않아 직접 URL 진입 시 신청/계약 상세로 리다이렉트 (MVDV-9452)
  const isIFC = useIFCUnsupportedRedirect(contractId);

  const [daily, setDaily] = useState<ContractReservationResponse>();
  const [monthly, setMonthly] = useState<ContractReservationResponse>();

  const reservationTimeData: any = useMemo(() => {
    if (daily && monthly) {
      let componentsArray = null;
      if (monthly?.components !== null) {
        componentsArray = monthly?.components.map((monthlyItem) => {
          daily?.components.forEach((dailyItem) => {
            if (monthlyItem.facilityType === dailyItem.facilityType) {
              monthlyItem = { ...monthlyItem, monthlyLimit: monthlyItem.limit, dailyLimit: dailyItem.limit, monthlyUsage: monthlyItem.usage } as any;
            }
          });
          delete monthlyItem.limit;
          // delete monthlyItem.usage;
          return monthlyItem;
        });
      }

      return { ...monthly, components: componentsArray };
    }
  }, [daily, monthly]);

  const { executeAsync: getReservationTime } = useApiOperation(getFacilityReservationTime);

  useEffect(() => {
    if (isIFC) {
      return;
    }

    const units: ContractReservationUnit[] = ["MONTHLY", "DAILY"];
    const fetchReservationTime = async (unit: ContractReservationUnit) => {
      const { data, status } = await getReservationTime({ contractId: String(contractId), unit });

      if (status >= 200 && status <= 299) {
        if (unit === "DAILY") {
          setDaily(data.data.status);
        } else {
          setMonthly(data.data.status);
        }
      }
    };

    units.forEach((unit: ContractReservationUnit) => {
      fetchReservationTime(unit);
    });
  }, [isIFC, contractId, getReservationTime]);

  const onChangeTimeValue = (value: string, type: "monthlyUsage" | "monthlyLimit" | "dailyLimit") => {
    let timeLimit = null;

    const facilityData = reservationTimeData?.components?.find((item: FrontContractFacilityData) => item.facilityType === value);
    switch (type) {
      case "monthlyUsage":
        timeLimit = facilityData?.monthlyUsage;
        break;
      case "monthlyLimit":
        timeLimit = facilityData?.monthlyLimit;
        break;
      case "dailyLimit":
        timeLimit = facilityData?.dailyLimit;
        break;
      default:
        break;
    }

    // return timeLimit === null ? "-" : timeLimit === 0 ? 0 : makeTimeString(timeLimit);
    return timeLimit === null ? t("unlimited") : timeLimit === undefined ? "-" : timeLimit === 0 ? t("zeroHours") : makeTimeString(timeLimit);
  };

  // IFC 환경에서는 리다이렉트되므로 화면을 렌더링하지 않음
  if (isIFC) {
    return null;
  }

  return (
    <div>
      {reservationTimeData && (
        <>
          <MetaTag title={getMypageTitle()} />
          <Header
            headerType="BACK"
            title={t("title")}
            onClickBackButton={() => {
              goBack();
            }}
          />
          <article className="facility-header ">
            <section className="content-layout">
              <div className="facility-title">
                <p>{t("usagePeriod")}</p>
                <div>
                  <span className=" font-weight-400 font15">
                    {monthly?.start !== null
                      ? `${moment(monthly?.start).format(ViewYmdFormat.YYYY_MM_DD)} ~ ${moment(monthly?.end)
                          .subtract(1, "days")
                          .format(ViewYmdFormat.YYYY_MM_DD)}`
                      : "-"}
                  </span>
                </div>
              </div>
              <div className="facility-description">
                <p>{monthly?.start ? t("resetsNotice") : ""}</p>
              </div>
            </section>
          </article>
          <article className=" top-divider">
            <section className="content-layout">
              <div className="facility-title mb20">
                <p>{t("usageTime")}</p>
              </div>

              {ReservationFacility.map((facility, idx: number) => (
                <div key={facility.value} className={`${idx === 1 && "divider-top-bottom"}`}>
                  <p className="font15 font-weight-600 mb10">{facility.label}</p>
                  <div className="facility-content">
                    <div className="mb4">
                      <span className="mr20 text-primary3">{t("monthlyUsage")}</span>
                      <span>{onChangeTimeValue(facility.value, "monthlyUsage")}</span>
                    </div>
                    {/* <div className="mb4">
                      <span className="mr20 text-primary3">월 한도</span>
                      <span>{onChangeTimeValue(facility.value, "monthlyLimit")}</span>
                    </div>
                    <div>
                      <span className="mr20 text-primary3">일 한도</span>
                      <span>{onChangeTimeValue(facility.value, "dailyLimit")}</span>
                    </div> */}
                  </div>
                </div>
              ))}
            </section>
          </article>
        </>
      )}
    </div>
  );
};

export default FacilityTime;
